<script setup>
  import {computed, inject, onBeforeMount, ref} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentRecordLabel from "@components/RecordLabel.vue";
  import {Head as InertiaHead, Link as InertiaLink, usePage} from "@inertiajs/vue3";
  import {MedLink} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "ComponentBreadcrumb",
    components: {ComponentRecordLabel},
  });

  const props = defineProps({
    links: {
      type: Object,
      default: () => ({}),
    },
  });
  const page = usePage();
  const privacy = inject("$privacy");
  const title = ref("");

  const processedLinks = computed(() => {
    return props.links
      .map((link) => {
        if (typeof link === "function") {
          return link();
        }

        return link;
      })
      .filter((link) => link !== null);
  });

  const hasPatientName = computed(() => props.links.some((link) => link?.encryptedLabel));

  const hasInputLabel = computed(() => props.links.some((link) => link.label === "Eingabe"));

  const hasAnalyseLabel = computed(() => props.links.some((link) => link.label === "Analyse"));

  onBeforeMount(() => {
    if (hasPatientName.value) {
      privacy.decryptPatient(page.props.patient).then((decryptPatient) => {
        if (decryptPatient.firstname) {
          title.value += decryptPatient.firstname;
        }

        if (decryptPatient.lastname) {
          title.value += " " + decryptPatient.lastname;
        }

        let anonymizedTitle = "Patient";

        if (hasInputLabel.value && !hasAnalyseLabel.value) {
          title.value += ", Eingabe";
          anonymizedTitle += ", Eingabe";
        } else if (hasInputLabel.value && hasAnalyseLabel.value) {
          title.value += ", Analyse";
          anonymizedTitle += ", Analyse";
        }

        // Force override any existing page title in Matomo
        globalThis._mtm?.push({"mtm.pageTitle": anonymizedTitle});
        globalThis._paq?.push(["setDocumentTitle", anonymizedTitle]);
      });
    }
  });
</script>

<template>
  <template v-if="hasPatientName">
    <inertia-head :title="title" />
  </template>

  <nav :class="$attrs.class" class="flex" aria-label="Breadcrumb">
    <ol class="flex items-center space-x-2">
      <li>
        <MedLink
          variant="secondary"
          class="flex hover:text-gray-900 hover:no-underline"
          :href="$route('dashboard.index')"
          data-test="breadcrumb-dashboard-inertia-link"
          icon="space_dashboard"
        />
      </li>

      <li v-for="(link, index) in processedLinks" :key="'breadcrumb-' + index">
        <div class="flex items-center">
          <component-icon class="text-gray-400">chevron_right</component-icon>

          <div class="relative ml-2 text-sm font-semibold">
            <component
              :is="link.url ? InertiaLink : 'span'"
              :href="link.url"
              :class="
                cn('block', {
                  'cursor-pointer text-gray-500 hover:text-gray-900 hover:underline': link.url,
                  'text-gray-900': !link.url,
                })
              "
              :data-test="`breadcrumb-${index}-item-${link.url ? 'inertia-link' : 'span'}`"
            >
              <div v-if="link.label" class="leading-4">
                <span v-text="link.label" />
              </div>

              <div v-else-if="link.encryptedLabel" class="line-clamp-3 max-w-80 leading-4">
                <template v-for="(label, i) in link.encryptedLabel" :key="'label-' + i">
                  <span :data-encrypted="label" />
                </template>
              </div>
            </component>

            <div
              v-if="link.subLabel !== null && typeof link.subLabel === 'string'"
              class="absolute text-xs font-normal whitespace-nowrap text-gray-500"
            >
              {{ link.subLabel }}
            </div>

            <div
              v-else-if="link.subLabel !== null && typeof link.subLabel === 'object'"
              class="absolute text-base font-normal"
            >
              <component :is="link.subLabel.component" v-bind="link.subLabel.props" />
            </div>
          </div>
        </div>
      </li>
    </ol>
  </nav>
</template>
